import {
  DEFAULT_ADDRESS_COUNTRY,
  mergeProfile,
  profileConflicts,
  updateProfileInput,
  type ProfileFields,
  type UpdateProfileInput,
} from '@gg/shared';

/** The profile form's values: every field as the text the inputs hold (P-11). */
export type ProfileFormValues = {
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  nationality: string;
  phone: string;
  address: { line1: string; line2: string; postalCode: string; city: string; country: string };
  clubName: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  licenceFederation: string;
  licenceNumber: string;
};

export type AddressPart = keyof ProfileFormValues['address'];
export type ProfileFormPath =
  Exclude<keyof ProfileFormValues, 'address'> | `address.${AddressPart}`;

export function formValuesOf(p: ProfileFields): ProfileFormValues {
  return {
    firstName: p.firstName ?? '',
    lastName: p.lastName ?? '',
    birthDate: p.birthDate ?? '',
    gender: p.gender ?? '',
    nationality: p.nationality ?? '',
    phone: p.phone ?? '',
    address: {
      line1: p.address?.line1 ?? '',
      line2: p.address?.line2 ?? '',
      postalCode: p.address?.postalCode ?? '',
      city: p.address?.city ?? '',
      country: p.address?.country ?? DEFAULT_ADDRESS_COUNTRY,
    },
    clubName: p.clubName ?? '',
    emergencyContactName: p.emergencyContactName ?? '',
    emergencyContactPhone: p.emergencyContactPhone ?? '',
    emergencyContactRelationship: p.emergencyContactRelationship ?? '',
    licenceFederation: p.licenceFederation ?? '',
    licenceNumber: p.licenceNumber ?? '',
  };
}

/** The stored value at a form path, as text (to compare with what the input holds). */
export function storedValue(p: ProfileFields, path: ProfileFormPath): string {
  const v = formValuesOf(p);
  return path.startsWith('address.')
    ? v.address[path.slice('address.'.length) as AddressPart]
    : (v[path as Exclude<ProfileFormPath, `address.${string}`>] as string);
}

export type FieldCheck =
  | { ok: true; body: UpdateProfileInput; value: string; unchanged: boolean }
  | { ok: false; error: string };

/**
 * Validates one field with the shared schema and the cross-field rules on the merged profile,
 * and builds its `PATCH /me/profile` body. `unchanged`: nothing to send.
 */
export function checkField(profile: ProfileFields, path: ProfileFormPath, raw: string): FieldCheck {
  const body = path.startsWith('address.')
    ? { address: { [path.slice('address.'.length)]: raw } }
    : { [path]: raw };
  const parsed = updateProfileInput.safeParse(body);
  if (!parsed.success) return { ok: false, error: parsed.error.issues[0]!.message };

  const merged = mergeProfile(profile, parsed.data);
  const conflicts = profileConflicts(merged);
  const conflict =
    path === 'address.postalCode' || path === 'address.country'
      ? conflicts.postalCode
      : path === 'phone' || path === 'emergencyContactPhone'
        ? conflicts.emergencyContactPhone
        : undefined;
  if (conflict) return { ok: false, error: conflict };

  const value = storedValue(merged as ProfileFields, path);
  return { ok: true, body: parsed.data, value, unchanged: value === storedValue(profile, path) };
}

/** The message of an API `invalid_input` for this field, if the API refused it. */
export function apiFieldError(details: unknown, path: ProfileFormPath): string | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as {
    fields?: Record<string, string[] | undefined>;
    issues?: { path: string; message: string }[];
  };
  const issue = d.issues?.find((i) => i.path === path);
  if (issue) return issue.message;
  const last = path.split('.').pop()!;
  return d.fields?.[path]?.[0] ?? d.fields?.[last]?.[0] ?? null;
}
